import { useEffect, useState } from 'react'

export type NativePickerOverride = 'auto' | 'always' | 'never'

/**
 * A React hook that detects whether the user is on a mobile device.
 *
 * This hook uses the CSS media query `(pointer: coarse)` to determine if the device
 * has a coarse pointing device (typically a touchscreen). It listens for changes
 * in the media query state and updates accordingly.
 *
 * @returns {boolean} `true` if the device has a coarse pointer (mobile/touch device),
 * `false` otherwise. Returns `false` during server-side rendering.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const isMobile = useIsMobileDevice();
 *
 *   return (
 *     <div>
 *       {isMobile ? 'Mobile View' : 'Desktop View'}
 *     </div>
 *   );
 * }
 * ```
 */
export function useIsMobileDevice(): boolean {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false
    return window.matchMedia('(pointer: coarse)').matches
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia('(pointer: coarse)')

    const handleChange = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [])

  return isMobile
}
